import {
  openModal,
  routeTo,
  routeBack,
  finish,
  partiallyUpdate
} from '../../core/actions';
import FlowHeader from '../../components/FlowHeader/FlowHeader';
import InvestmentOptionsContents from '../../components/InvestmentOptions/Contents';
import RolloverTypeContents from '../../components/RolloverTypeOptions/RolloverTypeContents';
import FooterSite from '../../components/FooterSite/FooterSite';
import BaseLayout from '../../components/BaseLayout/BaseLayout';
import SummitRewards from '../../components/BaseLayout/SummitRewards';
import {
  hasURLTickers,
  isValidEmail,
  isValidMaskedSSN,
  usernameValidation,
  passwordValidation,
  isAuthenticated,
  isValidDob,
  meetsMinimumInvestment,
  meetsMinimumInvestmentAPP,
  amountTooLowForAPP,
  compose,
  chose,
  chosenAddressValidation,
  isReviewMode,
  newFund,
  newAndExistingFund,
  hasExistingFunds,
  existingUserShouldGotoLogin,
  existingUserShouldGotoEnablement,
  isRDFPath,
  isPageClick,
  rdfRecommendationDiffers,
  isUsernameAlreadyTaken,
  choseResultingPath,
  userValidationFailed,
  isRPSRollover,
  isClosedFund,
  isValidFormerEmployer,
  taxWithholdingValidation,
  guestUserLoginRequired,
  showUpdateToIClassModal
} from '../shared/testers';

import RolloverChecklistScreen from '../../screens/ChecklistScreen/RolloverChecklistScreen';
import ProfileScreen from '../../screens/ProfileScreen/ProfileScreen';
import PersonalInformationScreen from '../../screens/PersonalInformationScreen/PersonalInformationScreen';
import FundingInstitutionScreen from '../../screens/FundingInstitutionScreen/FundingInstitutionScreen';
import CreateCredentialsScreen from '../../screens/CreateCredentialsScreen/CreateCredentialsScreen';
import ContactInformationScreen from '../../screens/ContactInformationScreen/ContactInformationScreen';
import ReviewScreen from '../../screens/ReviewScreen/ReviewScreen';
import ConfirmationScreen from '../../screens/ConfirmationScreen/ConfirmationScreen';
import RDFScreen from '../../screens/RDFScreen/RDFScreen';
import InvestmentReviewScreen from '../../screens/InvestmentReviewScreen/InvestmentReviewScreen';
import RDFTermsScreen from '../../screens/RDFTermsScreen/RDFTermsScreen';
import ActivelyManagedPortfolioScreen from '../../screens/ActivelyManagedPortfolioScreen/ActivelyManagedPortfolioScreen';
import BYOPScreen from '../../screens/BYOPScreen/BYOPScreen';
import MoneyMarketScreen from '../../screens/MoneyMarketScreen/MoneyMarketScreen';
import InvestmentOptionsScreen from '../../screens/InvestmentOptionsScreen/InvestmentOptionsScreen';
import RolloverTypeScreen from '../../screens/RolloverTypeScreen/RolloverTypeScreen';
import ExistingFundsScreen from '../../screens/ExistingFundsScreen/ExistingFundsScreen';
import RolloverGuideScreen from '../../screens/RolloverGuideScreen/RolloverGuideScreen';
import APPMinimumModal from '../../components/APPMinimumModal/APPMinimumModal';
import UserValidationModal from '../../components/UserValidationModal/UserValidationModal';
import DifferentRDFModal from '../../components/DifferentRDFModal/DifferentRDFModal';
import makeForcedLoginModal from '../../components/ForcedLoginModal/ForcedLoginModal';
import GuestUserLoginModal from '../../components/GuestUserLoginModal/GuestUserLoginModal';
import AddressValidationModal from '../../components/AddressValidationModal/AddressValidationModal';
import CredentialsValidationModal from '../../components/CredentialsValidationModal/CredentialsValidationModal';
import LearnMoreModal from '../../components/LearnMoreModal/LearnMoreModal';
import ClosedFundsModalWithRouting from '../../components/ClosedFundsModalWithRouting/ClosedFundsModalWithRouting';
import UpdateToIClassModal from '../../components/UpdateToIClass/UpdateToIClassModal';
import {
  commitCustodian,
  createApplication,
  makeLoadProfileData,
  commitRolloverType,
  loadClosedFunds,
  loadInvestmentDetails,
  updateAnalytics,
  loadExistingAgreements,
  loadLoginData
} from './loaders';
import {
  commitNewInvestmentsRDF,
  commitExistingInvestments,
  commitFinalizedInvestments,
  commitAmount,
  commitProfileData,
  commitPersonalInformation,
  commitContactInformation,
  commitRDFTermsConsent,
  commitReferrerForm,
  loadCustodians,
  loadTermsForRDF,
  loadTermsForSubmission,
  registerAndLoginUser,
  loadRDFiClassData,
  commitNewInvestmentsBYOP,
  commitShoppingFunds,
  loadMoneyMarketData,
  commitMoneyMarketFund
} from '../shared/loaders';
import { updateJourneyProfile } from '../return-journey/loaders';

import { FlowNode } from '../../core/types/flow';
import { registerFlow } from '../../core/flow-register';
import {
  contactTextFn,
  redirectToCLX,
  redirectToEnablement
} from '../shared/utils';
import { parseCurrentQueryString } from '../../core/util';

const nodes: Array<FlowNode> = [
  {
    route: 'landing',
    screen: RolloverChecklistScreen,
    load: loadLoginData,
    commit: commitShoppingFunds,
    next: {
      default: routeTo('profile')
    },
    flowProps: {
      displayStickyBanner: true,
      getContactText: contactTextFn(),
      analytics: {
        pageName: 'account-info'
      }
    }
  },
  {
    route: 'profile',
    screen: ProfileScreen,
    load: makeLoadProfileData(parseCurrentQueryString()),
    form: [
      {
        name: 'firstName',
        required: true
      },
      {
        name: 'lastName',
        required: true
      },
      {
        name: 'primaryEmailAddress',
        required: true,
        validation: isValidEmail
      },
      {
        name: 'dob',
        required: true,
        validation: isValidDob
      },
      {
        name: 'amount',
        required: true,
        validation: meetsMinimumInvestment
      }
    ],
    startsPersistingSensitive: true,
    commit: commitProfileData,
    next: {
      cases: [
        [hasURLTickers, routeTo('byop/review')],
        [
          compose(chose('skipLogin'), hasExistingFunds),
          routeTo('existingfunds')
        ],
        [chose('skipLogin'), routeTo('investment')],
        [guestUserLoginRequired, openModal(GuestUserLoginModal)],
        [hasExistingFunds, routeTo('existingfunds')]
      ],
      default: routeTo('investment')
    },
    flowProps: {
      getContactText: contactTextFn('PICI'),
      analytics: {
        pageName: 'About'
      }
    }
  },
  {
    route: 'existingfunds',
    screen: ExistingFundsScreen,
    next: {
      cases: [
        [newFund, routeTo('investment')],
        [newAndExistingFund, routeTo('byop')]
      ],
      default: routeTo('byop/review')
    },
    load: loadExistingAgreements,
    commit: commitExistingInvestments,
    flowProps: {
      getContactText: contactTextFn('Investment'),
      analytics: {
        pageName: 'ExistingFunds'
      }
    }
  },
  {
    route: 'investment',
    screen: InvestmentOptionsScreen,
    form: [
      {
        name: 'amount',
        required: true,
        validation: meetsMinimumInvestmentAPP
      }
    ],
    commit: commitAmount,
    next: {
      cases: [
        [chose('byop'), routeTo('byop')],
        [chose('rdf'), routeTo('rdf/terms')],
        [chose('money-market'), routeTo('money-market')],
        [compose(chose('app'), amountTooLowForAPP), openModal(APPMinimumModal)],
        [chose('app'), routeTo('app')]
      ],
      default: routeTo('byop')
    },
    flowProps: {
      getContactText: contactTextFn('Investment'),
      layoutClass: 'wide',
      title: 'How would you like to choose funds for your IRA?',
      options: InvestmentOptionsContents,
      analytics: {
        pageName: 'InvestmentOptions'
      }
    }
  },
  {
    route: 'byop',
    screen: BYOPScreen,
    commit: commitNewInvestmentsBYOP,
    next: {
      cases: [
        [showUpdateToIClassModal, openModal(UpdateToIClassModal)],
        [choseResultingPath('updateAndBack'), routeBack()]
      ],
      default: routeTo('byop/review')
    },
    flowProps: {
      getContactText: contactTextFn('Investment'),
      layoutClass: 'medium',
      analytics: {
        pageName: 'BuildPortfolio'
      }
    }
  },
  {
    route: 'byop/review',
    screen: InvestmentReviewScreen,
    commit: commitFinalizedInvestments,
    next: {
      cases: [
        [showUpdateToIClassModal, openModal(UpdateToIClassModal)],
        [isReviewMode(), routeTo('review')],
        [choseResultingPath('updateAndBack'), routeBack()],
        [choseResultingPath('reselect-funds'), routeTo('investment')]
      ],
      default: routeTo('information')
    },
    flowProps: {
      getContactText: contactTextFn('Investment'),
      layoutClass: 'wide',
      analytics: {
        pageName: 'BuildPortfolioSummary'
      }
    }
  },
  {
    route: 'rdf/terms',
    form: [
      {
        name: 'electronicAccessConsent',
        type: 'checkbox'
      },
      {
        name: 'advisoryServiceConsent',
        type: 'checkbox'
      }
    ],
    screen: RDFTermsScreen,
    load: loadTermsForRDF,
    commit: commitRDFTermsConsent,
    next: {
      default: routeTo('rdf/selection')
    },
    flowProps: {
      getContactText: contactTextFn('Investment'),
      analytics: {
        pageName: 'RetirementDateFundConsent'
      }
    }
  },
  {
    route: 'rdf/selection',
    screen: RDFScreen,
    load: loadRDFiClassData,
    commit: commitNewInvestmentsRDF,
    next: {
      cases: [
        [chose('byop'), routeTo('byop')],
        [isReviewMode(), routeTo('review')]
      ],
      default: routeTo('information')
    },
    flowProps: {
      getContactText: contactTextFn('Investment'),
      layoutClass: 'medium',
      analytics: {
        pageName: 'RetirementDateFundSelection'
      }
    }
  },
  {
    route: 'money-market',
    screen: MoneyMarketScreen,
    load: loadMoneyMarketData,
    commit: commitMoneyMarketFund,
    next: {
      cases: [[chose('viewAllMoneyMarketFunds'), routeTo('byop')]],
      default: routeTo('byop/review')
    },
    flowProps: {
      getContactText: contactTextFn('Investment'),
      layoutClass: 'medium',
      analytics: {
        pageName: 'MoneyMarketFund'
      }
    }
  },
  {
    route: 'information',
    screen: PersonalInformationScreen,
    form: [
      {
        name: 'firstName',
        required: true
      },
      {
        name: 'lastName',
        required: true
      },
      {
        name: 'middleName'
      },
      { name: 'citizenship', required: true },
      {
        name: 'dob'
      },
      {
        name: 'ssn',
        required: true,
        validation: isValidMaskedSSN
      }
    ],
    commit: commitPersonalInformation,
    next: {
      cases: [
        [userValidationFailed, openModal(UserValidationModal)],
        [
          compose(isRDFPath, isPageClick, rdfRecommendationDiffers),
          openModal(DifferentRDFModal)
        ],
        [
          existingUserShouldGotoEnablement,
          openModal(makeForcedLoginModal(redirectToEnablement))
        ],
        [
          existingUserShouldGotoLogin,
          openModal(makeForcedLoginModal(redirectToCLX))
        ],
        [isReviewMode(), routeTo('review')],
        [showUpdateToIClassModal, openModal(UpdateToIClassModal)]
      ],
      default: routeTo('contact/information')
    },
    flowProps: {
      getContactText: contactTextFn('PICI'),
      analytics: {
        pageName: 'PersonalInformation'
      },
      fullScreenModal: compose(isRDFPath, isPageClick, rdfRecommendationDiffers)
    }
  },
  {
    route: 'contact/information',
    screen: ContactInformationScreen,
    load: loadTermsForSubmission,
    form: [
      { name: 'residentialStreet1', required: true },
      { name: 'residentialStreet2' },
      { name: 'residentialZip', required: true },
      { name: 'residentialCity', required: true },
      { name: 'residentialState', required: true },
      { name: 'mailingStreet1', required: true },
      { name: 'mailingStreet2' },
      { name: 'mailingZip', required: true },
      { name: 'mailingCity', required: true },
      { name: 'mailingState', required: true },
      { name: 'primaryEmailAddress', required: true },
      { name: 'phone1', required: true },
      { name: 'phone2' },
      { name: 'phone3' },
      { name: 'mailingAddressDifferent', type: 'checkbox' },
      { name: 'useResidentialAddress', required: true },
      { name: 'useMailingAddress', required: true }
    ],
    commit: commitContactInformation,
    next: {
      cases: [
        [chosenAddressValidation, openModal(AddressValidationModal)],
        [isReviewMode(), routeTo('review')], // @TODO remove this when inline again
        [isRPSRollover(), routeTo('review')]
      ],
      default: routeTo('funding/method')
    },
    flowProps: {
      getContactText: contactTextFn('PICI'),
      analytics: {
        pageName: 'ContactInformation'
      }
    }
  },
  {
    route: 'funding/method',
    screen: RolloverTypeScreen,
    load: loadClosedFunds,
    commit: commitRolloverType,
    next: {
      cases: [
        [chose('LEARN_MORE'), openModal(LearnMoreModal)],
        [isClosedFund, openModal(ClosedFundsModalWithRouting)],
        [compose(chose('INDIRECT'), isAuthenticated()), routeTo('review')],
        [chose('INDIRECT'), routeTo('credentials')],
        [chose('BYOP'), routeTo('byop')]
      ],
      default: routeTo('funding/institution')
    },
    flowProps: {
      getContactText: contactTextFn('Funding'),
      layoutClass: 'wide',
      title: 'How will you be handling your rollover?',
      options: RolloverTypeContents,
      analytics: {
        pageName: 'RolloverFundingMethod'
      }
    }
  },
  {
    route: 'funding/institution',
    screen: FundingInstitutionScreen,
    load: loadCustodians,
    form: [
      { name: 'manualProviderName', required: true },
      {
        name: 'formerEmployerName',
        required: true,
        validation: isValidFormerEmployer
      }
    ],
    commit: commitCustodian,
    next: {
      cases: [[isAuthenticated(), routeTo('review')]],
      default: routeTo('credentials')
    },
    flowProps: {
      getContactText: contactTextFn('Funding'),
      layoutClass: 'wide',
      analytics: {
        pageName: 'InstitutionSelection'
      }
    }
  },
  {
    route: 'credentials',
    form: [
      {
        name: 'username',
        required: true,
        validation: usernameValidation
      },
      {
        name: 'password',
        required: true,
        validation: passwordValidation
      }
    ],
    screen: CreateCredentialsScreen,
    commit: registerAndLoginUser,
    next: {
      cases: [[isUsernameAlreadyTaken, openModal(CredentialsValidationModal)]],
      default: routeTo('review')
    },
    flowProps: {
      getContactText: contactTextFn('PICI'),
      analytics: {
        pageName: 'Enablement'
      }
    }
  },
  {
    route: 'review',
    screen: ReviewScreen,
    form: [
      {
        name: 'taxWithholdingOption'
      },
      {
        name: 'taxWithholdingPercent',
        required: true,
        validation: taxWithholdingValidation
      }
    ],
    load: loadTermsForSubmission,
    commit: createApplication,
    next: {
      cases: [
        [chose('updateCustomer'), partiallyUpdate()],
        [chose('byop'), routeTo('byop')],
        [chose('multiple'), routeTo('existingfunds')],
        [chose('existing_only'), routeTo('existingfunds')],
        [chose('info'), routeTo('information')],
        [chose('contact'), routeTo('contact/information')],
        [chose('rdf'), routeTo('rdf/selection')],
        [chose('funding'), routeTo('funding/method')]
      ],
      default: routeTo('confirmation')
    },
    flowProps: {
      getContactText: contactTextFn('Review'),
      analytics: {
        pageName: 'ReviewConfirm'
      }
    }
  },
  {
    route: 'confirmation',
    screen: ConfirmationScreen,
    load: loadInvestmentDetails,
    commit: commitReferrerForm,
    next: {
      cases: [
        [chose('referrer'), partiallyUpdate()],
        [chose('guide'), routeTo('guide')]
      ],
      default: finish()
    },
    flowProps: {
      analytics: {
        pageName: 'Confirmation',
        isComplete: true
      }
    }
  },
  {
    route: 'app',
    screen: ActivelyManagedPortfolioScreen,
    next: {
      default: finish()
    },
    flowProps: {
      analytics: {
        pageName: 'ActivePlus'
      }
    }
  },
  {
    route: 'guide',
    screen: RolloverGuideScreen,
    next: {
      default: finish()
    },
    flowProps: {
      analytics: {
        pageName: 'RolloverGuide'
      }
    }
  }
];

export default registerFlow({
  namespace: 'mutual-fund/retirement/rollover',
  nodes,
  start: 'landing',
  pageDecorators: [BaseLayout, SummitRewards],
  onLoad: updateAnalytics,
  onCommit: updateJourneyProfile,
  flowProps: {
    getContactText: contactTextFn(),
    header: FlowHeader,
    footer: FooterSite
  }
});
